import React, { useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { useGSAP } from "@gsap/react";
import { Helmet } from "react-helmet-async";
import "./AboutPage.css";

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin, useGSAP);

/* ───────── Data ───────── */

const milestones = [
  {
    year: "2014",
    title: "A quiet beginning",
    desc: "ISTP Overseas Education is founded in Vijayawada with one conviction — global education should not be gated by confusion or cost.",
  },
  {
    year: "2016",
    title: "First 100 students",
    desc: "Our first major milestone, earned quietly: one hundred students placed across international universities with 100% visa success.",
  },
  {
    year: "2019",
    title: "Expanded services",
    desc: "We add dedicated immigration support, IELTS coaching partnerships, and structured pre-departure orientation programs.",
  },
  {
    year: "2022",
    title: "500+ placements",
    desc: "Students now placed across the USA, UK, Canada, Australia, and beyond — the result of a decade of deep university relationships.",
  },
  {
    year: "2025",
    title: "1,000+ success stories",
    desc: "A full decade in, we close the year with a 100% visa success rate — and a commitment to keep earning that number.",
  },
];

const team = [
  { initials: "ST", name: "Sai Teja Regalla", role: "Founder & Chief Consultant" },
  { initials: "TP", name: "Teja Priya Garimella", role: "Senior Visa Advisor" },
  { initials: "AY", name: "Anuhya Yalamanchali", role: "University Relations" },
  { initials: "AT", name: "Amrita Tomar", role: "Student Success Manager" },
];

const values = [
  {
    num: "01",
    title: "Integrity First",
    desc: "We give honest profile assessments — even when they are not what you hoped to hear. Every recommendation earns its place.",
  },
  {
    num: "02",
    title: "End-to-End Support",
    desc: "From profile evaluation to post-landing assistance, we walk beside you through every step of the journey.",
  },
  {
    num: "03",
    title: "Student-Centric",
    desc: "Your goals, your timeline, your budget. Every plan is built for you — never pulled off a template.",
  },
  {
    num: "04",
    title: "Proven Results",
    desc: "1,000+ placements and a 100% visa success rate in 2025 — credentials that speak before we do.",
  },
];

/* ───────── Component ───────── */

export default function AboutPage() {
  const container = useRef(null);

  useGSAP(
    () => {
      /* ═══ HERO ═══ */
      const heroTl = gsap.timeline({ defaults: { ease: "power4.out" } });
      heroTl
        .to(".hero-eyebrow", { opacity: 1, y: 0, duration: 0.9 }, 0.15)
        .to(
          ".hero-title .line-inner",
          { y: 0, duration: 1.35, stagger: 0.1 },
          0.25
        )
        .to(".hero-sub", { opacity: 1, y: 0, duration: 0.95 }, 1.0)
        .to(".hero-ctas", { opacity: 1, y: 0, duration: 0.95 }, 1.15)
        .to(".hero-meta", { opacity: 1, duration: 0.85 }, 1.3);

      // Scroll-cue line pulse
      gsap.to(".scroll-indicator__line", {
        scaleY: 0,
        transformOrigin: "top",
        duration: 1.6,
        repeat: -1,
        ease: "power2.inOut",
      });

      /* ═══ CONNECTOR — scrubbed plane flight ═══ */
      const pathEl = document.querySelector("#planePath");
      if (pathEl) {
        const length = pathEl.getTotalLength();
        gsap.set(pathEl, {
          strokeDasharray: length,
          strokeDashoffset: length,
        });

        gsap.to(pathEl, {
          strokeDashoffset: 0,
          ease: "none",
          scrollTrigger: {
            trigger: ".connector-section",
            start: "top bottom",
            end: "bottom top",
            scrub: 1.2,
          },
        });

        gsap.to(".paper-plane", {
          motionPath: {
            path: "#planePath",
            align: "#planePath",
            alignOrigin: [0.5, 0.5],
            autoRotate: true,
          },
          ease: "none",
          scrollTrigger: {
            trigger: ".connector-section",
            start: "top bottom",
            end: "bottom top",
            scrub: 1.2,
          },
        });
      }

      gsap.to(".connector-content .js-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: { trigger: ".connector-section", start: "top 65%" },
      });
      gsap.to(".connector-content .line-inner", {
        y: 0,
        duration: 1.25,
        stagger: 0.12,
        ease: "power4.out",
        scrollTrigger: { trigger: ".connector-section", start: "top 60%" },
      });

      /* ═══ MISSION ═══ */
      gsap.to(".mission .js-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: { trigger: ".mission", start: "top 70%" },
      });
      gsap.to(".mission-title .line-inner", {
        y: 0,
        duration: 1.3,
        stagger: 0.1,
        ease: "power4.out",
        scrollTrigger: { trigger: ".mission", start: "top 65%" },
      });
      gsap.to(".mission-text p", {
        opacity: 1,
        y: 0,
        stagger: 0.15,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".mission-text", start: "top 75%" },
      });
      // Parallax on mission image
      gsap.fromTo(
        ".mission-image img",
        { yPercent: -8 },
        {
          yPercent: 8,
          ease: "none",
          scrollTrigger: {
            trigger: ".mission-image",
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        }
      );

      /* ═══ STATS — counters ═══ */
      gsap.to(".stats .js-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".stats", start: "top 70%" },
      });
      gsap.to(".stat-card", {
        opacity: 1,
        y: 0,
        stagger: 0.1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".stats-grid", start: "top 80%" },
      });
      gsap.utils.toArray(".stat-number").forEach((el) => {
        const target = parseFloat(el.dataset.target);
        const suffix = el.dataset.suffix || "";
        const obj = { val: 0 };
        gsap.to(obj, {
          val: target,
          duration: 2.4,
          ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 82%" },
          onUpdate: () => {
            el.innerText = Math.floor(obj.val).toLocaleString() + suffix;
          },
        });
      });

      /* ═══ VALUES ═══ */
      gsap.to(".values .js-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".values", start: "top 70%" },
      });
      gsap.to(".value-card", {
        opacity: 1,
        y: 0,
        stagger: 0.12,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".values-grid", start: "top 80%" },
      });

      /* ═══ HORIZONTAL TIMELINE — desktop only ═══ */
      const mm = gsap.matchMedia();
      mm.add("(min-width: 769px)", () => {
        const track = document.querySelector(".horizontal-track-content");
        if (!track) return;

        const getScrollAmount = () =>
          track.scrollWidth - window.innerWidth;

        gsap.to(track, {
          x: () => -getScrollAmount(),
          ease: "none",
          scrollTrigger: {
            trigger: ".horizontal-section",
            start: "top top",
            end: () => `+=${getScrollAmount()}`,
            pin: true,
            scrub: 1,
            invalidateOnRefresh: true,
          },
        });
      });

      /* ═══ TEAM ═══ */
      gsap.to(".team .js-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".team", start: "top 70%" },
      });
      gsap.to(".team-card", {
        opacity: 1,
        y: 0,
        stagger: 0.1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".team-grid", start: "top 80%" },
      });

      /* ═══ CTA ═══ */
      gsap.to(".cta-content > *", {
        opacity: 1,
        y: 0,
        stagger: 0.14,
        duration: 1.1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".cta", start: "top 70%" },
      });
    },
    { scope: container }
  );

  return (
    <div className="about-page" ref={container}>

      <Helmet>
        <title>About Us | ISTP Overseas Education Consultants Vijayawada</title>
        <meta name="description" content="Learn about ISTP Overseas — Vijayawada's trusted study abroad consultants helping students get into top universities across UK, Canada, Australia, Germany and more." />
        <link rel="canonical" href="https://www.istpoverseas.com/about" />
        <meta property="og:title" content="About ISTP Overseas" />
        <meta property="og:description" content="Vijayawada's trusted overseas education consultancy with 500+ successful placements." />
        <meta property="og:url" content="https://www.istpoverseas.com/about" />
      </Helmet>
      {/* ═══════ HERO ═══════ */}
      <section className="hero">
        <div className="hero-inner">
          <span className="hero-eyebrow">
            ISTP Overseas Education &nbsp;·&nbsp; Est. 2014 &nbsp;·&nbsp; Vijayawada
          </span>

          <h1 className="hero-title">
            <span className="line-mask"><span className="line-inner">A decade of turning</span></span>
            <span className="line-mask"><span className="line-inner"><em>distant dreams</em></span></span>
            <span className="line-mask"><span className="line-inner">into destinations.</span></span>
          </h1>

          <p className="hero-sub">
            Since 2014, we have walked 1,000+ students from their first
            &ldquo;what if&rdquo; all the way to landing in their dream
            university — with honesty, expertise, and unwavering commitment
            to their success.
          </p>

          <div className="hero-ctas">
            <Link to="/book-slot" className="btn-primary">
              Book free consultation
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
            <Link to="/student-meets" className="btn-ghost">
              Upcoming webinars
            </Link>
          </div>

          <div className="hero-meta">
            <div className="hero-meta-item">
              <span className="hero-meta-num">1,000+</span>
              <span className="hero-meta-label">Students Placed</span>
            </div>
            <div className="hero-meta-item">
              <span className="hero-meta-num">15+</span>
              <span className="hero-meta-label">Countries</span>
            </div>
            <div className="hero-meta-item">
              <span className="hero-meta-num">150+</span>
              <span className="hero-meta-label">University Partners</span>
            </div>
            <div className="hero-meta-item">
              <span className="hero-meta-num">100%</span>
              <span className="hero-meta-label">2025 Visa Success</span>
            </div>
          </div>
        </div>

        {/* <div className="scroll-indicator" aria-hidden="true">
          <span>Scroll</span>
          <div className="scroll-indicator__line" />
        </div> */}
      </section>

      {/* ═══════ CONNECTOR — scrubbed plane flight ═══════ */}
      <section className="connector-section">
        <svg
          className="plane-path-svg"
          viewBox="0 0 1200 600"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {/* Graceful curve from top-left of the section to bottom-right */}
          <path
            id="planePath"
            d="M -40 120 C 280 60, 500 460, 780 300 S 1140 120, 1260 520"
          />
        </svg>

        <div className="paper-plane" aria-hidden="true">
          <svg viewBox="0 0 40 40" width="38" height="38">
            <path
              d="M2 20 L38 4 L28 38 L20 24 L2 20 Z"
              fill="currentColor"
            />
            <path
              d="M20 24 L38 4"
              stroke="rgba(0,0,0,0.18)"
              strokeWidth="0.8"
              fill="none"
            />
          </svg>
        </div>

        <div className="connector-content">
          <span className="section-tag js-reveal">The Journey</span>
          <h2>
            <span className="line-mask"><span className="line-inner">Every great destination</span></span>
            <span className="line-mask"><span className="line-inner">begins with <em>a single step.</em></span></span>
          </h2>
        </div>
      </section>

      {/* ═══════ MISSION ═══════ */}
      <section className="mission">
        <div className="mission-inner">
          <div className="mission-text">
            <span className="section-tag js-reveal">Our Mission</span>
            <h2 className="mission-title">
              <span className="line-mask"><span className="line-inner">We don&rsquo;t just</span></span>
              <span className="line-mask"><span className="line-inner">process applications.</span></span>
              <span className="line-mask"><span className="line-inner">We build <em>futures.</em></span></span>
            </h2>
            <p>
              Every student deserves access to world-class education —
              regardless of their background, city, or starting point. Our
              mission is to remove every barrier between you and your dream
              university: confusion, paperwork, visa anxiety, and financial
              uncertainty.
            </p>
            <p>
              We combine deep institutional knowledge, strong university
              relationships, and genuine care for each student to deliver
              outcomes that speak for themselves.
            </p>
          </div>

          <div className="mission-image">
            <img
              src="assets/mission2.png"
              alt="A student smiling on graduation day"
            />
            <div className="mission-image-badge">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>100% visa success in 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ STATS ═══════ */}
      <section className="stats">
        <div className="stats-header">
          <span className="section-tag js-reveal">By The Numbers</span>
          <h2 className="js-reveal">
            A decade measured in <em>outcomes,</em> not promises.
          </h2>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-number" data-target="1000" data-suffix="+">0</span>
            <span className="stat-label">Students Placed</span>
          </div>
          <div className="stat-card">
            <span className="stat-number" data-target="150" data-suffix="+">0</span>
            <span className="stat-label">University Partners</span>
          </div>
          <div className="stat-card">
            <span className="stat-number" data-target="15" data-suffix="+">0</span>
            <span className="stat-label">Countries</span>
          </div>
          <div className="stat-card">
            <span className="stat-number" data-target="100" data-suffix="%">0</span>
            <span className="stat-label">2025 Visa Success</span>
          </div>
        </div>
      </section>

      {/* ═══════ VALUES ═══════ */}
      <section className="values">
        <div className="values-header">
          <span className="section-tag js-reveal">What We Stand For</span>
          <h2 className="js-reveal">
            Four principles guiding <em>every</em> recommendation.
          </h2>
        </div>
        <div className="values-grid">
          {values.map((v, i) => (
            <div className="value-card" key={i}>
              <span className="value-num">{v.num}</span>
              <h3>{v.title}</h3>
              <p>{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ HORIZONTAL TIMELINE ═══════ */}
      <section className="horizontal-section">
        <div className="horizontal-track-content">
          <div className="horizontal-intro">
            <span className="section-tag">Our Journey</span>
            <h2>
              A decade<br />in <em>milestones.</em>
            </h2>
            <p>
              Scroll through ten years of steady, honest work — each milestone
              earned one student at a time.
            </p>
          </div>

          <div className="horizontal-track">
            {milestones.map((m, i) => (
              <div className="milestone-card" key={i}>
                <span className="milestone-index">
                  {String(i + 1).padStart(2, "0")} / {String(milestones.length).padStart(2, "0")}
                </span>
                <div className="milestone-year">{m.year}</div>
                <div className="milestone-body">
                  <h3>{m.title}</h3>
                  <p>{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ TEAM ═══════ */}
      <section className="team">
        <div className="team-header">
          <span className="section-tag js-reveal">The People</span>
          <h2 className="js-reveal">
            Navigators behind every <em>success story.</em>
          </h2>
        </div>
        <div className="team-grid">
          {team.map((t, i) => (
            <div className="team-card" key={i}>
              <div className="team-avatar">{t.initials}</div>
              <h3>{t.name}</h3>
              <span>{t.role}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="cta">
        <div className="cta-content">
          <h2>
            Your story starts with<br />
            <em>one conversation.</em>
          </h2>
          <p>
            Book a free profile evaluation and get a personalised roadmap to
            your dream university — no obligations, no pressure, just honest
            guidance.
          </p>
          <div>
            <Link to="/book-slot" className="btn-primary">
              Book free counselling
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}